import { Component } from '@theme/component';

/**
 * @typedef {Object} ColorSelectionDetail
 * @property {string} colorRef
 * @property {string} colorName
 * @property {string} colorHex
 * @property {string} colorId
 * @property {string} colorBase
 * @property {string} [colorHandle]
 * @property {string} [colorCollections]
 * @property {string} [colorFilters]
 * @property {string} [colorImage]
 * @property {HTMLElement} [sourceElement]
 */

const COLOR_SELECTION_SELECTORS = {
  dialog: '.tint-color-picker__dialog',
  applyButton: '.color-browse-grid__apply-btn',
  clearButton: '.tint-color-picker__clear',
  pickerButton: '.tint-color-picker__button',
  swatch: '.tint-color-picker__color-swatch[data-color-hex]',
  ref: '.tint-color-picker__color-ref[data-color-ref]',
  name: '.tint-color-picker__color-name[data-color-name]',
  chooseText: '.tint-color-picker__choose-text[data-color-ref-name]',
  collectionText: '.tint-color-picker__collection-text[data-color-collection]',
  closeButton: '[ref="closeButton"]',
  card: '.color-browse-grid__card',
};

const COLOR_SELECTION_EVENTS = {
  selected: 'color_selected',
  confirmed: 'color_selection_confirmed',
  cleared: 'color_selection_cleared',
};

const COLOR_SELECTION_CLASSES = {
  selected: 'is-selected',
};

/**
 * Build a ColorSelectionDetail object from a color card button.
 *
 * @param {HTMLElement} card
 * @returns {ColorSelectionDetail}
 */
const getColorDetailFromCard = (card) => ({
  colorRef: card.getAttribute('data-color-ref') || '',
  colorName: card.getAttribute('data-color-name') || '',
  colorHex: card.getAttribute('data-color-hex') || '',
  colorId: card.getAttribute('data-color-id') || '',
  colorBase: card.getAttribute('data-color-base') || '',
  colorHandle: card.getAttribute('data-color-handle') || '',
  colorCollections: card.getAttribute('data-color-collections') || '',
  colorFilters: card.getAttribute('data-color-filters') || '',
  colorImage: card.getAttribute('data-color-image') || '',
  sourceElement: card,
});

/**
 * Update the visible selected color state on the picker button.
 *
 * @param {HTMLElement} swatchEl
 * @param {HTMLElement} refEl
 * @param {HTMLElement} nameEl
 * @param {HTMLElement | null} pickerButton
 * @param {HTMLButtonElement | null} clearButton
 * @param {HTMLElement | null} chooseTextEl
 * @param {HTMLElement | null} collectionTextEl
 * @param {ColorSelectionDetail} detail
 */
const applyColorSelectionToPicker = (
  swatchEl,
  refEl,
  nameEl,
  pickerButton,
  clearButton,
  chooseTextEl,
  collectionTextEl,
  detail,
) => {
  const { colorHex, colorRef, colorName } = detail;

  if (!colorHex || !colorRef || !colorName) return;

  swatchEl.style.backgroundColor = colorHex;
  refEl.textContent = colorRef;
  nameEl.textContent = colorName;

  if (chooseTextEl) {
    // Show a combined label like "PE098 California Love"
    chooseTextEl.textContent = `${colorRef} ${colorName}`.trim();
  }

  if (collectionTextEl) {
    /** @type {string} */
    const rawCollections = detail.colorCollections || '';

    if (rawCollections) {
      const firstHandle = rawCollections.split(',')[0]?.trim();

      if (firstHandle) {
        // Convert handle-style string (e.g. "original-colours") into a
        // human-friendly label (e.g. "Original colours").
        const humanLabel = firstHandle
          .replace(/-/g, ' ')
          .replace(/^([a-z])/, (match) => match.toUpperCase());

        collectionTextEl.textContent = humanLabel;
      } else {
        collectionTextEl.textContent = colorRef;
      }
    } else {
      collectionTextEl.textContent = colorRef;
    }
  }

  if (pickerButton) {
    pickerButton.classList.add(COLOR_SELECTION_CLASSES.selected);
  }

  if (clearButton) {
    clearButton.hidden = false;
  }
};

/**
 * Reset the picker UI back to its initial, unselected state.
 *
 * @param {HTMLElement} swatchEl
 * @param {HTMLElement} refEl
 * @param {HTMLElement} nameEl
 * @param {HTMLElement | null} pickerButton
 * @param {HTMLButtonElement | null} clearButton
 * @param {HTMLButtonElement | null} applyButton
 * @param {string} initialRefText
 * @param {string} initialNameText
 * @param {HTMLElement | null} chooseTextEl
 * @param {HTMLElement | null} collectionTextEl
 * @param {string} initialChooseText
 * @param {string} initialCollectionText
 */
const resetPickerSelection = (
  swatchEl,
  refEl,
  nameEl,
  pickerButton,
  clearButton,
  applyButton,
  initialRefText,
  initialNameText,
  chooseTextEl,
  collectionTextEl,
  initialChooseText,
  initialCollectionText,
) => {
  if (applyButton) {
    applyButton.disabled = true;
  }

  swatchEl.style.backgroundColor = 'transparent';
  refEl.textContent = initialRefText;
  nameEl.textContent = initialNameText;

  if (chooseTextEl) {
    chooseTextEl.textContent = initialChooseText;
  }

  if (collectionTextEl) {
    collectionTextEl.textContent = initialCollectionText;
  }

  if (pickerButton) {
    pickerButton.classList.remove(COLOR_SELECTION_CLASSES.selected);
  }

  if (clearButton) {
    clearButton.hidden = true;
  }
};

class TintColorPicker extends Component {
  connectedCallback() {
    super.connectedCallback();

    const dialog = this.querySelector(COLOR_SELECTION_SELECTORS.dialog);
    const applyButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.applyButton)
    );
    const clearButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.clearButton)
    );
    const pickerButton = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.pickerButton)
    );
    const swatchEl = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.swatch)
    );
    const refEl = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.ref)
    );
    const nameEl = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.name)
    );
    const chooseTextEl = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.chooseText)
    );
    const collectionTextEl = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.collectionText)
    );
    const closeButton = /** @type {HTMLElement | null} */ (
      this.querySelector(COLOR_SELECTION_SELECTORS.closeButton)
    );

    if (!dialog || !applyButton || !swatchEl || !refEl || !nameEl) return;

    /** @type {ColorSelectionDetail | null} */
    let selectedColor = null;

    const initialRefText = refEl.textContent || '';
    const initialNameText = nameEl.textContent || '';
    const initialChooseText = chooseTextEl?.textContent || '';
    const initialCollectionText = collectionTextEl?.textContent || '';

    // Handle clicks on color cards to select a color and dispatch color_selected
    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const card = /** @type {HTMLElement | null} */ (
        target.closest(COLOR_SELECTION_SELECTORS.card)
      );

      if (!card || !dialog.contains(card)) return;

      const currentCards = dialog.querySelectorAll(COLOR_SELECTION_SELECTORS.card);
      for (const button of currentCards) {
        const el = /** @type {HTMLElement} */ (button);
        el.setAttribute('aria-checked', el === card ? 'true' : 'false');
      }

      const detail = getColorDetailFromCard(card);

      const eventSelected = new CustomEvent(COLOR_SELECTION_EVENTS.selected, {
        bubbles: true,
        detail,
      });

      card.dispatchEvent(eventSelected);
    });

    // Enable Apply button and store selected color when a color is selected
    this.addEventListener(COLOR_SELECTION_EVENTS.selected, (event) => {
      /** @type {{ detail?: ColorSelectionDetail }} */
      // @ts-ignore - TS not available in theme build, but helps editors
      const customEvent = event;
      const detail = customEvent.detail;

      if (!detail) return;

      selectedColor = detail;

      applyButton.disabled = false;
    });

    // Apply button confirms selection: close dialog and dispatch color_selection_confirmed
    applyButton.addEventListener('click', () => {
      if (!selectedColor) return;

      if (closeButton instanceof HTMLElement) {
        closeButton.click();
      } else if (dialog instanceof HTMLDialogElement) {
        dialog.close();
      }

      const eventConfirmed = new CustomEvent(COLOR_SELECTION_EVENTS.confirmed, {
        bubbles: true,
        detail: selectedColor,
      });

      applyButton.dispatchEvent(eventConfirmed);
    });

    // Update visible selected color when selection is confirmed
    this.addEventListener(COLOR_SELECTION_EVENTS.confirmed, (event) => {
      /** @type {{ detail?: ColorSelectionDetail }} */
      // @ts-ignore
      const customEvent = event;
      const detail = customEvent.detail;

      if (!detail) return;

      applyColorSelectionToPicker(
        swatchEl,
        refEl,
        nameEl,
        pickerButton,
        clearButton,
        chooseTextEl,
        collectionTextEl,
        detail,
      );
    });

    // Optional: clear selection and reset UI state
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        selectedColor = null;

        resetPickerSelection(
          swatchEl,
          refEl,
          nameEl,
          pickerButton,
          clearButton,
          applyButton,
          initialRefText,
          initialNameText,
          chooseTextEl,
          collectionTextEl,
          initialChooseText,
          initialCollectionText,
        );

        const eventCleared = new CustomEvent(COLOR_SELECTION_EVENTS.cleared, {
          bubbles: true,
        });

        clearButton.dispatchEvent(eventCleared);
      });
    }
  }
}

customElements.define('tint-color-picker', TintColorPicker);

